import React, { useCallback, useState } from 'react';
import { GridApi, GridReadyEvent, ICellRendererParams } from 'ag-grid-community';
import * as XLSX from 'xlsx';
import CommonButton from '../../../components/common-components/button/CommonButton';
import styles from './Persons.module.css';
import toolbarStyles from '../../../styles/ToolbarButtons.module.css';
import { Plus, FileSpreadsheet, RotateCw, Pencil, Trash2 } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../../hooks/useStore';
import NewPersonPopup from './parts/NewPersonPopup';
import EditPersonPopup from './parts/EditPersonPopup';
import DeleteConfirmationDialog from '../hospital-files/parts/delete/DeleteConfirmationDialog';
import { IPerson } from '../../../models/person/PersonModel';
import { toast } from 'react-toastify';
import BaseAgGrid, {
  type GridFetchParams,
} from '../../../components/common-components/grid/BaseAgGrid';
import { usePersonsColumnDefs, usePersonsDefaultColDef } from './Persons.grid';

const ActionCellRenderer = (props: ICellRendererParams<IPerson>) => {
  const { popupStore, personStore, authStore } = useStore();
  const isAdmin = authStore.isAdmin;

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.data) {
      personStore.setSelectedPersonToEdit(props.data);
      popupStore.setIsEditPersonOpen(true);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isAdmin) {
      toast.error('Bu işlem için admin yetkisi gereklidir.');
      return;
    }
    if (props.data?.id) {
      personStore.setSelectedPersonToDelete(props.data.id);
      popupStore.setIsDeletePersonConfirmationOpen(true);
    }
  };

  return (
    <div className={styles.actionCell}>
      <button onClick={handleEdit} className={styles.editButton} title="Düzenle">
        <Pencil size={20} />
      </button>
      <button
        onClick={handleDelete}
        className={`${styles.deleteButton} ${!isAdmin ? styles.disabledButton : ''}`}
        title={isAdmin ? 'Sil' : 'Bu işlem için admin yetkisi gereklidir'}
        disabled={!isAdmin}
      >
        <Trash2 size={20} />
      </button>
    </div>
  );
};

const Persons = () => {
  const { popupStore, personStore } = useStore();

  const fetchRows = useCallback(
    async (params: GridFetchParams) => {
      const result = await personStore.listAllPersons(params);
      return { rows: result.rows, lastRow: result.lastRow };
    },
    [personStore]
  );

  const onGridReady = useCallback(
    (params: GridReadyEvent<IPerson>) => {
      personStore.setGridApi(params.api);
    },
    [personStore]
  );

  const columnDefs = usePersonsColumnDefs(ActionCellRenderer);
  const defaultColDef = usePersonsDefaultColDef();

  const handleNewPerson = () => {
    popupStore.setIsNewPersonOpen(true);
  };

  const exportToExcel = useCallback(
    async (api: GridApi<IPerson> | null) => {
      if (!api) return;

      api.showLoadingOverlay();

      try {
        const result = await personStore.listAllPersons({
          startRow: 0,
          endRow: 1000000,
          filterModel: api.getFilterModel(),
          sortModel: null,
        });

        if (!result?.rows.length) {
          api.hideOverlay();
          toast.warning("Excel'e aktarılacak veri bulunamadı");
          return;
        }

        const exportData = result.rows.map(item => ({
          ID: item.id,
          İsim: item.name,
          Hastane: item.hospitalName,
          'Telefon Numarası': item.phoneNumber,
          'E-mail': item.email || '',
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Kişiler');
        XLSX.writeFile(wb, `Kisiler_${new Date().toISOString().split('T')[0]}.xlsx`);

        toast.success(`${exportData.length} kişi Excel dosyasına aktarıldı`);
      } catch (error) {
        console.error('Excel export error:', error);
        toast.error("Excel'e aktarılırken bir hata oluştu");
      } finally {
        api.hideOverlay();
      }
    },
    [personStore]
  );

  const handleDeleteConfirm = async () => {
    if (personStore.selectedPersonToDelete) {
      await personStore.deletePerson(personStore.selectedPersonToDelete);
      popupStore.setIsDeletePersonConfirmationOpen(false);
      personStore.setSelectedPersonToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    popupStore.setIsDeletePersonConfirmationOpen(false);
    personStore.setSelectedPersonToDelete(null);
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.gridContainer}>
          <BaseAgGrid<IPerson>
            fetchRows={fetchRows}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            title="Kişiler"
            toolbarLeft={({ api, refresh }) => (
              <>
                <CommonButton type="primary" onClick={handleNewPerson}>
                  <Plus size={20} />
                  Yeni Kişi
                </CommonButton>
                <CommonButton
                  type="tertiary"
                  onClick={() => exportToExcel(api)}
                  leftIcon={<FileSpreadsheet size={18} />}
                  className={toolbarStyles.toolbarBtnExcel}
                >
                  Excel'e Aktar
                </CommonButton>
                <CommonButton
                  type="refresh"
                  onClick={refresh}
                  leftIcon={<RotateCw size={18} />}
                  className={toolbarStyles.toolbarBtnRefresh}
                >
                  Yenile
                </CommonButton>
              </>
            )}
            paginationPageSize={20}
          />
        </div>
      </div>
      {popupStore.isNewPersonOpen && <NewPersonPopup />}
      {popupStore.isEditPersonOpen && <EditPersonPopup />}
      <DeleteConfirmationDialog
        isOpen={popupStore.isDeletePersonConfirmationOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Kişi Silme Onayı"
      />
    </>
  );
};

export default observer(Persons);
